import { useQuery } from "react-query";
import FullWidthContainer from "../../components/FullWidthContainer";
import useAuthData from "../../auth/useAuthData";
import { useTeachers } from "../../states/cache-state/useTeachers";
import GenericTeacherCard from "../../components/teacher/GenericTeacherCard";
import WrapList from "../../components/ui-lib/WrapList";
import { Input } from "../../components/ui-lib/Input";
import useDebounce from "../../hooks/useDebounce";
import { useMemo, useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import UiDialog from "../../components/ui-lib/UiDialog";
import { Teacher } from "../../models/teacher/teacher";
import TeacherDetails from "../../components/teacher/TeacherDetails";
import { useQueryParams } from "../../hooks/useQueryParams";
import { TeacherFilterParams } from "../../models/filters";
import { QueryParamNames } from "../../models/enums/query-param-names";

function TeacherOverview() {
  const { currentSchool } = useAuthData();
  const { getTeachersOfSchool } = useTeachers();
  const [activeTeacher, setActiveTeacher] = useState<Teacher | undefined>();
  const { getQueryParamGroup, setQueryParams } = useQueryParams();

  const queryParams = useMemo(
    () => getQueryParamGroup(TeacherFilterParams),
    [getQueryParamGroup],
  );

  const { data: teachers } = useQuery(
    ["teachers", currentSchool?.id],
    () => getTeachersOfSchool(currentSchool!.id),
    {
      enabled: !!currentSchool,
    },
  );
  const debouncedHandleSearchChange = useDebounce(
    (v: string) =>
      setQueryParams((p) => {
        !!v
          ? p.set(QueryParamNames.searchTerm, v)
          : p.delete(QueryParamNames.searchTerm);
        return p;
      }),
    500,
  );
  const filteredTeachers = useMemo(() => {
    return teachers?.filter((t) =>
      t.fullName
        .toLowerCase()
        .includes(queryParams?.searchTerm?.toLowerCase() ?? ""),
    );
  }, [teachers, queryParams?.searchTerm]);

  return (
    <FullWidthContainer className="space-y-4 bg-nid_grey">
      <h1 className="text-th-primary-900">
        {currentSchool?.name} Lehrpersonen
      </h1>
      <div>
        <Input
          id="search-teacher"
          defaultValue={queryParams?.searchTerm}
          label="Lehrpersonen suchen"
          name="search-teacher"
          placeholder="Lehrpersonen suchen"
          onChange={(event) => debouncedHandleSearchChange(event.target.value)}
        ></Input>
      </div>
      <WrapList items={filteredTeachers || []}>
        {(teacher) => (
          <GenericTeacherCard
            teacher={teacher}
            className="duration-300 hover:cursor-pointer hover:shadow-md"
            onClick={() => setActiveTeacher(teacher)}
          >
            <GenericTeacherCard.Action>
              <button onClick={() => setActiveTeacher(teacher)}>
                <InformationCircleIcon className="h-5 w-5 text-th-primary-900" />
              </button>
            </GenericTeacherCard.Action>
          </GenericTeacherCard>
        )}
      </WrapList>
      <UiDialog
        open={!!activeTeacher}
        onClose={() => setActiveTeacher(undefined)}
        title="Lehrperson"
      >
        {activeTeacher && <TeacherDetails teacher={activeTeacher} />}
      </UiDialog>
    </FullWidthContainer>
  );
}

export default TeacherOverview;
